import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { ThemeColorSchemeType, ThemeColorType, useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import { DEFAULT_THEME_COLOR } from '@/components/sections/builder/theme/color';
import ThemeColorPreview from '@/components/sections/builder/theme/color/scheme-preview';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import ColorSchemeForm from '@/components/sections/builder/theme/color/scheme-form';

type SchemeSettingType = {
  value?: ThemeColorSchemeType;
  onChange?: (value?: ThemeColorSchemeType) => void;
};

export default function SchemeSetting({ value, onChange }: SchemeSettingType) {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const color = builder.theme?.color || DEFAULT_THEME_COLOR;
  const colorSchemes = color.colorSchemes || [];

  const setColor = (value: Partial<ThemeColorType>) => {
    builder.setTheme({
      color: { ...color, ...value },
    });
  };

  const onChangeScheme = (scheme: Partial<ThemeColorSchemeType>) => {
    const existingScheme = colorSchemes.find((v) => v.id === scheme.id);

    if (!existingScheme) return;

    const updatedScheme = {
      ...existingScheme,
      ...scheme,
    };

    setColor({
      colorSchemes: colorSchemes.map((v) => (v.id === updatedScheme.id ? updatedScheme : v)),
    });

    onChange?.(updatedScheme);
  };

  return (
    <>
      <div className='rounded-md bg-gray-50 py-4'>
        {value && (
          <div className='flex items-center gap-x-4 px-4 pb-4'>
            <div className='w-20'>
              <ThemeColorPreview scheme={value} />
            </div>
            <div>
              <div className='text-sm'>Scheme {value.id}</div>
              <div className='mt-1 flex items-center gap-x-1'>
                <Popover>
                  <PopoverTrigger>
                    <div className='text-xs text-primary'>Edit</div>
                  </PopoverTrigger>
                  <PopoverContent>
                    <ColorSchemeForm
                      scheme={value}
                      onChange={(e) => {
                        onChangeScheme({
                          ...e,
                          id: Number(value.id),
                        });
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <div className='text-gray-300'>|</div>
                <button type='button' onClick={() => onChange?.()} className='text-xs text-primary'>
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
        <div className='px-4'>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='outline'
                className='flex w-full gap-x-2 bg-white !text-sm font-medium'
              >
                <span>Change</span>
                <ChevronDown className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='max-h-[400px] w-full overflow-y-auto'>
              {colorSchemes.map((scheme) => (
                <DropdownMenuItem key={scheme.id} onClick={() => onChange?.(scheme)}>
                  <div className='flex items-center gap-x-4'>
                    <div className='w-20'>
                      <ThemeColorPreview scheme={scheme} />
                    </div>
                    <div className='w-36'>Scheme {scheme.id}</div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </>
  );
}
